import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { animateScroll as scroll } from 'react-scroll'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import Flag from 'react-world-flags'

const Navbar = () => {
  const { t, i18n } = useTranslation()
  const [activeSection, setActiveSection] = useState('beranda')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const navbarRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const languages = [
    { code: 'id', flag: 'id', label: 'ID' },
    { code: 'en', flag: 'gb', label: 'EN' }
  ]

  // Change language handler
  const changeLanguage = code => {
    i18n.changeLanguage(code)
  }

  // Navigate to different sections
  const handleNavigation = section => {
    setActiveSection(section)
    setDrawerOpen(false)

    if (section === 'kandidat') {
      navigate('/kandidat')
      return
    }

    if (section === 'artikel') {
      navigate('/artikel')
      return
    }

    if (['karir', 'faq'].includes(section)) {
      navigate(`/${section}`)
    } else if (location.pathname === '/') {
      scrollToSection(section)
    } else {
      navigate('/')
      setTimeout(() => scrollToSection(section), 100)
    }
  }

  // Scroll to specific section
  const scrollToSection = section => {
    const element = document.getElementById(section)
    if (element) {
      const offset = navbarRef.current ? navbarRef.current.clientHeight : 0
      const targetPosition =
        element.getBoundingClientRect().top + window.scrollY - offset

      scroll.scrollTo(targetPosition, { duration: 500, smooth: true })
    }
  }

  const sections = [
    'beranda',
    'layanan',
    'tentang',
    'galeri',
    'kontak',
    'faq',
    'karir',
    'kandidat',
    'artikel'
  ]

  useEffect(() => {
    const path = location.pathname
    if (path.startsWith('/kandidat')) {
      setActiveSection('kandidat')
    } else if (path.startsWith('/artikel')) {
      setActiveSection('artikel')
    } else if (path === '/karir') {
      setActiveSection('karir')
    } else if (path === '/faq') {
      setActiveSection('faq')
    } else if (path === '/') {
      setActiveSection('beranda')
    }
  }, [location.pathname])

  useEffect(() => {
    const handleScroll = () => {
      const offset = navbarRef.current ? navbarRef.current.clientHeight : 0
      const scrollPosition = window.scrollY + offset + 50

      for (let section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const top = element.offsetTop
          const height = element.offsetHeight

          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <AppBar
      position='fixed'
      ref={navbarRef}
      sx={{ backgroundColor: '#0056B3', zIndex: 1300 }}
    >
      <Toolbar>
        {isMobile ? (
          <>
            <IconButton color='inherit' onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor='left'
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  width: '60vw', // Adjust width for mobile
                  backgroundColor: '#0056B3',
                  color: 'white',
                  position: 'absolute',
                  top: '55px', // Ensure it doesn't overlap AppBar
                  zIndex: 1299 // Drawer behind navbar
                }
              }}
            >
              <List>
                {sections.map(section => (
                  <ListItem key={section} disablePadding>
                    <ListItemButton onClick={() => handleNavigation(section)}>
                      <ListItemText
                        primary={t(`navbar.${section}`)}
                        sx={{
                          color:
                            activeSection === section ? '#FF9500' : 'white',
                          fontWeight:
                            activeSection === section ? 'bold' : 'normal'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Drawer>
          </>
        ) : (
          <Grid container justifyContent='center'>
            {sections.map(section => (
              <Grid item key={section} sx={{ margin: '0 10px' }}>
                <Button
                  onClick={() => handleNavigation(section)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '18px',
                    color: activeSection === section ? '#FF9500' : 'white',
                    fontWeight: activeSection === section ? 'bold' : 'normal',
                    '&:hover': { color: '#FF9500' }
                  }}
                >
                  {t(`navbar.${section}`)}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}
        <div style={{ position: 'absolute', right: 10 }}>
          {languages.map(({ code, flag, label }) => (
            <Button
              key={code}
              onClick={() => changeLanguage(code)}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontWeight: 'bold',
                marginLeft: { xs: 2, sm: 5 }, // Adjust margin for mobile (xs) and desktop (sm+)
                '& .language-label': {
                  color: i18n.language === code ? '#FF9500' : 'white' // Highlight text
                },
                '&:hover .language-label': {
                  color: '#FF9500'
                }
              }}
            >
              <Flag
                code={flag}
                style={{
                  width: 24,
                  height: 16,
                  marginRight: 8
                }}
              />
              <span className='language-label'>{label}</span>
            </Button>
          ))}
        </div>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
